import * as React from "react";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VisibilityIcon from "@mui/icons-material/Visibility";

export interface MobileSectionPickerItem {
  /** Stable identifier — passed back to the picker's onSelect handler. */
  id: string;
  /** Display label. */
  label: string;
  /** Section icon (matches the desktop SectionNav icon). */
  icon: React.ReactNode;
  /** Pre-formatted dollar total or count, e.g. "$9,999,999" or "2 notes". */
  trailing?: string;
}

export interface MobileSectionPickerProps {
  items: MobileSectionPickerItem[];
  /** When true, rows show an eye icon instead of a chevron (review state). */
  readOnly?: boolean;
  onSelect: (id: string) => void;
}

/**
 * Vertical list of section rows for the mobile Details tab.
 *
 * Tapping a row opens that section full-width (MobileSectionView).  Each row
 * shows the same icon used in the desktop SectionNav drawer, the section name,
 * an optional trailing summary (dollar total / count), and a chevron.
 *
 * In review mode (`readOnly` true), the chevron is swapped for an eye icon so
 * the user knows fields are locked.  Selection still works — the section
 * just opens with all fields disabled.
 */
export function MobileSectionPicker({
  items,
  readOnly = false,
  onSelect,
}: MobileSectionPickerProps) {
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {readOnly ? "Details — read only" : "Details"}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {items.map((item, index) => (
          <ListItem
            key={item.id}
            disablePadding
            sx={{
              borderBottom: index === items.length - 1 ? "none" : "1px solid",
              borderColor: "divider",
            }}
          >
            <ListItemButton
              onClick={() => onSelect(item.id)}
              sx={{ minHeight: 48, px: 1.5, py: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: "body2" }}
              />
              {item.trailing && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#25D72C",
                    fontWeight: 500,
                    mr: 1,
                  }}
                >
                  {item.trailing}
                </Typography>
              )}
              {readOnly ? (
                <VisibilityIcon sx={{ color: "text.disabled", fontSize: 18 }} />
              ) : (
                <ChevronRightIcon sx={{ color: "text.disabled" }} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </Paper>
    </Box>
  );
}
